import React from "react";
import { Building2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { GST_STATES, normalizeGstin } from "@/lib/gstBilling";

// Optional B2B billing block — sits under the guest's name/phone on both checkouts.
export const GstInvoiceFields = ({ value, onChange, error, idPrefix = "" }) => {
  const set = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-2xl border border-black/10 bg-paper-muted/40 p-3.5" data-testid={`${idPrefix}gst-block`}>
      <label htmlFor={`${idPrefix}gst_toggle`} className="flex items-center gap-2.5 cursor-pointer select-none">
        <input
          id={`${idPrefix}gst_toggle`} type="checkbox" checked={!!value.enabled}
          onChange={(e) => set({ enabled: e.target.checked })}
          data-testid={`${idPrefix}gst-toggle`}
          className="w-4 h-4 accent-[var(--brand,#E0393E)] rounded border-black/20 cursor-pointer"
        />
        <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink">
          <Building2 className="w-4 h-4 text-ink-faint" /> Need a GST invoice for your company?
        </span>
      </label>

      {value.enabled && (
        <div className="mt-3.5 space-y-3" data-testid={`${idPrefix}gst-fields`}>
          <div>
            <Label htmlFor={`${idPrefix}gst_company`} className="text-xs text-ink-soft">Company name</Label>
            <Input
              id={`${idPrefix}gst_company`} value={value.company} onChange={(e) => set({ company: e.target.value })}
              placeholder="Acme Technologies Pvt Ltd" data-testid={`${idPrefix}gst-company`}
              className="mt-1 h-11 bg-white focus-visible:ring-brand"
            />
          </div>
          <div>
            <Label htmlFor={`${idPrefix}gst_gstin`} className="text-xs text-ink-soft">GSTIN</Label>
            <Input
              id={`${idPrefix}gst_gstin`} value={value.gstin} onChange={(e) => set({ gstin: normalizeGstin(e.target.value) })}
              placeholder="18AABCU9603R1ZM" maxLength={15} autoCapitalize="characters" spellCheck={false}
              data-testid={`${idPrefix}gst-gstin`}
              className={`mt-1 h-11 bg-white font-mono tracking-wide uppercase focus-visible:ring-brand ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`}
            />
            {error && <p className="mt-1.5 text-xs font-medium text-red-600" data-testid={`${idPrefix}gst-error`}>{error}</p>}
          </div>
          <div>
            <Label htmlFor={`${idPrefix}gst_address`} className="text-xs text-ink-soft">Billing address</Label>
            <Textarea
              id={`${idPrefix}gst_address`} value={value.address} onChange={(e) => set({ address: e.target.value })}
              rows={2} placeholder="Registered office address" data-testid={`${idPrefix}gst-address`}
              className="mt-1 bg-white focus-visible:ring-brand resize-none"
            />
          </div>
          <div>
            <Label htmlFor={`${idPrefix}gst_state`} className="text-xs text-ink-soft">State</Label>
            <select
              id={`${idPrefix}gst_state`} value={value.state} onChange={(e) => set({ state: e.target.value })}
              data-testid={`${idPrefix}gst-state`}
              className="mt-1 h-11 w-full rounded-md border border-black/15 bg-white px-3 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="">Select state</option>
              {GST_STATES.map((s) => <option key={s.code} value={s.code}>{s.name} ({s.code})</option>)}
            </select>
          </div>
          <p className="text-[11px] leading-relaxed text-ink-soft" data-testid={`${idPrefix}gst-note`}>
            The invoice will be issued to the company. Whether GST credit is available depends on the room's tax rate — ask your accountant.
          </p>
        </div>
      )}
    </div>
  );
};

export default GstInvoiceFields;
